import { Col, Divider, Form, InputNumber, Row, Select } from 'antd';
import { FC } from 'react';

import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetTitle from '@/libs/antd/components/Title';
import { inputCurrency, inputCurrencyWithDot } from '@/libs/antd/input-number';
import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import rule from '@/libs/antd/rules';
import { SPFFacilityPayload } from '@/libs/fleet-api/services/spf';
import { useForm } from '@/libs/antd/form';
import { MasterData } from '@/libs/fleet-api/services/master-data';

type FacilityModalParam =
	| {
			action: 'create';
	  }
	| {
			action: 'edit';
			record: SPFFacilityPayload;
	  };

export const useFacilityModal = () =>
	useAsyncModal<FacilityModalParam, SPFFacilityPayload>();

export type FacilityModalProps = ReturnType<typeof useFacilityModal> &
	Partial<
		Record<
			| 'assetTypeOptions'
			| 'financingObjectConditionOptions'
			| 'plafonTypeOptions'
			| 'tenorOptions'
			| 'insuranceOptions',
			MasterData[]
		>
	> & {
		userGroup?: string;
	};

const FacilityModal: FC<FacilityModalProps> = ({
	modal,
	data,
	close,
	assetTypeOptions,
	financingObjectConditionOptions,
	insuranceOptions,
	plafonTypeOptions,
	tenorOptions,
	userGroup,
}) => {
	const { form, fieldName } = useForm<SPFFacilityPayload>();

	return (
		<Modal
			{...modal}
			width={940}
			closable={false}
			buttonPosition="end"
			cancelText="BACK"
			okText="SAVE"
			onCancel={() => close()}
			onOk={form.submit}
			afterOpenChange={(isOpen) => {
				if (isOpen && data?.action === 'edit') {
					form.setFieldsValue(data.record);
				} else {
					form.resetFields();
				}
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>Add Detail Fasilitas</FleetTitle>
			</div>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={close}
			>
				<Form.Item
					name={fieldName('id')}
					noStyle
				/>
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('assetTypeId')}
							label="Jenis Aset"
							rules={[rule.required()]}
							required
						>
							<Select
								options={
									assetTypeOptions?.map((option) => ({
										label: option.value,
										value: option.id,
									})) ?? []
								}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('financingObjectConditionId')}
							label="Kondisi Obyek Pembiayaan"
							rules={[rule.required()]}
							required
						>
							<Select
								options={
									financingObjectConditionOptions?.map((option) => ({
										label: option.value,
										value: option.id,
									})) ?? []
								}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('plafonTypeId')}
							label="Jenis Plafond"
							rules={[rule.required()]}
							required
						>
							<Select
								options={
									plafonTypeOptions?.map((option) => ({
										label: option.value,
										value: option.id,
									})) ?? []
								}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('financingLimitNature')}
							label="Sifat Limit Pembiayaan"
							rules={[rule.required()]}
							required
						>
							<Select
								options={[
									{ label: 'Revolving', value: 'Revolving' },
									{ label: 'Non Revolving', value: 'Non Revolving' },
								]}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('tenorId')}
							label="Tenor Pembiayaan"
							rules={[rule.required()]}
							required
						>
							<Select
								options={
									tenorOptions?.map((option) => ({
										label: option.value,
										value: option.id,
									})) ?? []
								}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('interestRateIndicationPercent')}
							label="Indikasi Suku Bunga"
							rules={[rule.required()]}
							required
						>
							<InputNumber
								{...inputCurrencyWithDot}
								suffix="%"
								min={0}
								max={100}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('downPaymentPercent')}
							label="Down Payment"
							rules={[rule.required()]}
							required
						>
							<InputNumber
								{...inputCurrencyWithDot}
								suffix="%"
								min={0}
								max={100}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('securityDeposit')}
							label="Security Deposit"
							rules={userGroup === 'FRM' ? [] : [rule.required()]}
							required={userGroup !== 'FRM'}
						>
							<InputNumber {...inputCurrency} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('administrativeFee')}
							label="Biaya Administrasi"
							rules={[rule.required()]}
							required
						>
							<InputNumber {...inputCurrency} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('provisionFee')}
							label="Provisi"
							rules={[rule.required()]}
							required
						>
							<InputNumber
								{...inputCurrencyWithDot}
								suffix="%"
								min={0}
								max={100}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('serviceFee')}
							label="Biaya Layanan"
							rules={[rule.required()]}
							required
						>
							<InputNumber
								{...inputCurrencyWithDot}
								suffix="%"
								min={0}
								max={100}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('insuranceId')}
							label="Asuransi"
							rules={[rule.required()]}
							required
						>
							<Select
								options={
									insuranceOptions?.map((option) => ({
										label: option.value,
										value: option.id,
									})) ?? []
								}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('fiduciaryFee')}
							label="Fidusia"
							rules={[rule.required()]}
							required
						>
							<InputNumber {...inputCurrency} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('insuranceDiscountPercentage')}
							label="Discount Asuransi"
							initialValue={0}
							rules={[
								{
									type: 'number',
									max: 25,
									message: 'Maksimal 25%',
								},
							]}
						>
							<InputNumber
								{...inputCurrencyWithDot}
								suffix="%"
								min={0}
								max={25}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
			</Form>
		</Modal>
	);
};

export default FacilityModal;
